import React, { Component } from 'react';
import styled from 'styled-components';
import { Colors } from 'modules/theme';
import { push } from 'modules/history';
import { LibraryTypes, LibraryConfigTypes, LibrarySensorTypes } from 'constants/index';
import { listHeaderPrimaryButtonStyles } from 'constants/listHeaderButtons';
import InputSearch from 'components/InputSearch';
import H4 from 'components/H4';
import ButtonText from 'components/ButtonText';
import PaginationButtons from 'components/PaginationButtons';
import Table, { THeader, TableTitle, TRow, TableItem, TBody } from 'components/Table';
import Filter, { FilterGroup, FilterContainer } from 'components/Filter';
import Dropdown from 'components/Dropdown';
import Modal from 'components/Modal';
import SelectableButton from 'components/SelectableButton';
import AlertConfirm from 'components/AlertConfirm';
import ListEmpty from 'components/ListEmpty';
import Loader from 'components/Loader';
import PropTypes from 'prop-types';

const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding-bottom: 20px;
  gap: 16px;
  min-height: 44px;
  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 380px;
  display: flex;
  align-items: center;
  align-self: center;
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    margin-right: 12px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  align-self: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableContainer = styled.div`
  padding-bottom: 50px;
  margin-bottom: 50px;
  max-width: 100%;
  overflow: hidden;
  @media (max-width: 767px) {
    padding-bottom: 80px;
  }
`;

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 250px;
  right: 0;
  padding: 0 24px 25px 24px;
  background-color: ${Colors.white};
  box-sizing: border-box;
  @media (max-width: 700px) {
    left: 60px;
  }
`;

const ItemRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const ItemContainer = styled.div`
  flex-direction: column;
  padding: 15px;
  width: ${props => props.width || '50%'};
  @media (max-width: 768px) {
    width: 100% !important;
  }
`;

export default class LibraryListView extends Component {
  state = {
    dropdownOpenId: null,
    librarySelected: null,
    isOpenModal: false,
    isOpenAlert: false,
    search: '',
    type: '',
  };

  filterOnChange = event => {
    const { name, value } = event.target;

    this.setState({ [name]: value }, () => {
      const { fetchLibraries, fetchDeviceConfigurationUsage } = this.props;
      const { search, type } = this.state;

      // Check if we're filtering by device configurations
      const isDeviceConfigFilter = LibraryConfigTypes.some(config => config.value === type);

      if (isDeviceConfigFilter) {
        // Use device configuration usage endpoint for device configurations
        fetchDeviceConfigurationUsage({ search, type });
      } else {
        // Use regular libraries endpoint for other types
        fetchLibraries({ search, type });
      }
    });
  };

  searchOnChange = search => {
    this.setState({ search }, () => {
      const { fetchLibraries, fetchDeviceConfigurationUsage } = this.props;
      const { search, type } = this.state;

      // Check if we're filtering by device configurations
      const isDeviceConfigFilter = LibraryConfigTypes.some(config => config.value === type);

      if (isDeviceConfigFilter) {
        // Use device configuration usage endpoint for device configurations
        fetchDeviceConfigurationUsage({ search, type });
      } else {
        // Use regular libraries endpoint for other types
        fetchLibraries({ search, type });
      }
    });
  };

  switchDropdown = key => {
    const { dropdownOpenId } = this.state;
    const id = dropdownOpenId !== key ? key : null;

    this.setState({ dropdownOpenId: id });
  };

  switchModal = () => {
    const { isOpenModal } = this.state;

    this.setState({ isOpenModal: !isOpenModal });
  };

  switchAlert = (librarySelected = null) => {
    const { isOpenAlert } = this.state;

    this.setState({ isOpenAlert: !isOpenAlert, librarySelected });
  };

  editLibrary = ({ reference, type_code, type, id }) => {
    const configItem = LibraryConfigTypes.find(cls => cls.label === type);
    const isConfigType = !!configItem;

    const isSensorType = type_code
      ? LibrarySensorTypes.some(cls => type_code.includes(cls.value))
      : false;

    if (isConfigType) {
      push(`/libraries/edit/configuration/${id}/${configItem.value}`);
    } else if (isSensorType) {
      push(`/libraries/edit/sensor/${reference}/${type_code}`);
    } else {
      push(`/libraries/edit/${reference}/${type_code}`);
    }
  };

  remove = () => {
    const { removeLibrary } = this.props;
    const {
      librarySelected: { id, reference, type_code },
    } = this.state;

    removeLibrary(id, reference, type_code);
    this.switchAlert();
  };

  renderItem = (item, key) => {
    const { type } = this.state;
    const isDeviceConfigFilter = LibraryConfigTypes.some(config => config.value === type);
    const showLocationCount = isDeviceConfigFilter && item.locationCount !== undefined;

    return (
      <TRow key={key}>
        <TableItem
          width={showLocationCount ? '40%' : '50%'}
          color={Colors.lightBlue}
          textAlign="left"
        >
          {item.name}
        </TableItem>
        <TableItem width={showLocationCount ? '35%' : '45%'} textAlign="left">
          {item.type}
        </TableItem>
        {showLocationCount && (
          <TableItem width="20%" textAlign="center">
            {item.locationCount}
          </TableItem>
        )}
        <TableItem width="5%" isComponent>
          <Dropdown
            usePortal
            icon="ellipsis-v"
            options={[
              {
                title: 'Edit',
                icon: 'pencil-alt',
                color: Colors.green,
                action: () => this.editLibrary(item),
              },
              {
                title: 'Remove',
                icon: 'trash',
                color: Colors.red,
                action: () => this.switchAlert(item),
              },
            ]}
            isDropdownOpen={this.state.dropdownOpenId === key}
            onClickDropdownButton={() => this.switchDropdown(key)}
          />
        </TableItem>
      </TRow>
    );
  };

  renderAlertConfirm = () => {
    const { isOpenAlert, librarySelected } = this.state;

    return (
      <AlertConfirm
        title="Remove library"
        onCancel={() => this.switchAlert()}
        onSuccess={this.remove}
        successText="Remove"
        isVisible={isOpenAlert}
      >
        <H4 style={{ textAlign: 'center' }}>
          {`Are you sure that can remove the "${
            librarySelected ? librarySelected.name : ''
          }" library`}
        </H4>
      </AlertConfirm>
    );
  };

  goToPage = page => {
    this.setState({ isOpenModal: false }, () => {
      push(`/libraries/create/${page}`);
    });
  };

  renderModal = () => {
    const { isOpenModal } = this.state;

    return (
      <Modal
        title="What you would like to add?"
        onClose={this.switchModal}
        isVisible={isOpenModal}
        size="small"
      >
        <ItemRow>
          <ItemContainer>
            <SelectableButton action={() => this.goToPage('water-filter')} text="Filter System" />
          </ItemContainer>
          <ItemContainer>
            <SelectableButton
              action={() => this.goToPage('reverse-osmosis-filter')}
              text="Reverse Osmosis"
            />
          </ItemContainer>
        </ItemRow>
        <ItemRow>
          <ItemContainer>
            <SelectableButton
              action={() => this.goToPage('replacement-filter')}
              text="Replacement Filter"
            />
          </ItemContainer>
          <ItemContainer>
            <SelectableButton
              action={() => this.goToPage('device-configuration')}
              text="Device configuration"
            />
          </ItemContainer>
        </ItemRow>
        <ItemRow>
          <ItemContainer>
            <SelectableButton action={() => this.goToPage('softener')} text="Softener" />
          </ItemContainer>
          <ItemContainer>
            <SelectableButton action={() => this.goToPage('brine-drum')} text="Brine Drum" />
          </ItemContainer>
        </ItemRow>
        <ItemRow>
          <ItemContainer>
            <SelectableButton action={() => this.goToPage('ro-membrane')} text="RO Membrane" />
          </ItemContainer>
          <ItemContainer>
            <SelectableButton action={() => this.goToPage('catridge')} text="Cartridge" />
          </ItemContainer>
        </ItemRow>
        <ItemContainer>
          <SelectableButton action={() => this.goToPage('sensor')} text="Sensor" />
        </ItemContainer>
        <ItemRow />
      </Modal>
    );
  };

  render() {
    const {
      list,
      currentPage,
      pages,
      loading,
      fetchLibraries,
      deviceConfigurationUsage,
    } = this.props;
    const { type, search } = this.state;

    // Determine which data to use and if we should show location count
    const isDeviceConfigFilter = LibraryConfigTypes.some(config => config.value === type);
    const currentData =
      isDeviceConfigFilter && deviceConfigurationUsage
        ? deviceConfigurationUsage
        : { list, pages, currentPage, loading };
    const showLocationCount =
      isDeviceConfigFilter &&
      deviceConfigurationUsage &&
      deviceConfigurationUsage.list.some(item => item.locationCount !== undefined);

    return (
      <Container>
        <Header>
          <SearchContainer>
            <InputSearch onChange={this.searchOnChange} value={search} name="search" />
          </SearchContainer>
          <ButtonContainer>
            <ButtonGroup>
              <ButtonText
                theme="primary"
                text="New"
                icon="plus"
                customStyles={listHeaderPrimaryButtonStyles}
                onClick={this.switchModal}
              />
            </ButtonGroup>
            {/*
              <ButtonGroup>
                <ButtonText theme="default" text="Settings" icon="sliders-h" />
              </ButtonGroup>
            */}
          </ButtonContainer>
        </Header>
        <FilterGroup>
          <FilterContainer>
            <Filter
              title="Device Type:"
              selectedValue={type}
              name="type"
              handleChange={this.filterOnChange}
              options={[{ label: 'All', value: '' }, ...LibraryTypes]}
            />
          </FilterContainer>
        </FilterGroup>
        <TableContainer>
          <Table>
            <THeader>
              <TableTitle width={showLocationCount ? '40%' : '50%'} textAlign="left">
                Name
              </TableTitle>
              <TableTitle width={showLocationCount ? '35%' : '45%'} textAlign="left">
                Type
              </TableTitle>
              {showLocationCount && <TableTitle width="20%">Locations Using</TableTitle>}
              <TableTitle width="5%" />
            </THeader>
            <TBody>
              {currentData.loading ? (
                <Loader size={100} block />
              ) : (
                currentData.list.map((item, key) => this.renderItem(item, key))
              )}
            </TBody>
          </Table>
          {(!currentData.list || !currentData.list.length) && !currentData.loading && (
            <ListEmpty message="There are currently no libraries" />
          )}
        </TableContainer>
        <Footer>
          <PaginationButtons
            currentPage={currentData.currentPage}
            changePage={page => {
              if (isDeviceConfigFilter) {
                this.props.fetchDeviceConfigurationUsage({ page, search, type });
              } else {
                fetchLibraries({ page, search, type });
              }
            }}
            totalPages={currentData.pages}
          />
        </Footer>
        {this.renderModal()}
        {this.renderAlertConfirm()}
      </Container>
    );
  }
}

LibraryListView.propTypes = {
  currentPage: PropTypes.number,
  deviceConfigurationUsage: PropTypes.object,
  fetchDeviceConfigurationUsage: PropTypes.func,
  fetchLibraries: PropTypes.func,
  list: PropTypes.array,
  loading: PropTypes.bool,
  pages: PropTypes.number,
  removeLibrary: PropTypes.func,
};
